import { apiSlice } from './apiSlice';
import type { Ontology } from '../pages/ontologyPlayground/data/ontology';
import type { DataBinding } from '../pages/ontologyPlayground/data/ontology';
import { normalizeString } from '../utils/stringUtils';

export interface GeneratedOntology {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  tags: string[];
  author: string;
  source: 'generated';
  ontologyId: string;
  sourceId?: string | null;
  sourceType: string;
  generationStatus: string;
  embeddingStatus: string;
  confidenceScore?: number | null;
  entityCount: number;
  relationshipCount: number;
  createdAt?: string | null;
  ontology: Ontology;
  bindings: DataBinding[];
}

export interface OntologyGenerationStepPlan {
  number: 1 | 2 | 3;
  key: 'llm' | 'processing' | 'embedding';
  name: string;
  will_run: boolean;
  status: 'pending' | 'skipped' | 'blocked' | 'running' | 'completed' | 'failed';
  reason: string;
}

export interface OntologyGenerationPlan {
  source_type: string;
  source_id: string | null;
  ontology_id?: string | null;
  resume_from: 'full' | 'processing' | 'embedding' | 'complete' | 'blocked';
  can_generate?: boolean;
  reason: string;
  force: boolean;
  generation_status?: string | null;
  embedding_status?: string | null;
  error_message?: string | null;
  ontology_updated_at?: string | null;
  schema_updated_at?: string | null;
  has_ontology_raw: boolean;
  has_ontology_json: boolean;
  has_active_generation?: boolean;
  steps: OntologyGenerationStepPlan[];
}

interface GeneratedOntologiesResponse {
  count?: number;
  entries?: GeneratedOntology[];
}

interface GeneratedOntologiesQueryArgs {
  sourceType?: string;
  customerId?: string | null;
  clientId?: string | null;
  requireActiveSource?: boolean;
}

export interface OntologyQueryResponse {
  interpretation?: string;
  matchedEntities?: string[];
  matchedRelationships?: string[];
  topMatch?: {
    name: string;
    icon?: string;
    description?: string;
    properties?: Array<{ name: string; type: string }>;
  };
}

export const ontologyApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGeneratedOntologies: builder.query<GeneratedOntology[], GeneratedOntologiesQueryArgs | void>({
      query: (args) => ({
        url: '/api/v1/ontologies',
        method: 'GET',
        params: {
          ...(args?.sourceType ? { source_type: args.sourceType } : {}),
          ...(args?.customerId ? { customer_id: args.customerId } : {}),
          ...(args?.clientId ? { client_id: args.clientId } : {}),
          ...(args?.requireActiveSource ? { require_active_source: 'true' } : {}),
        },
      }),
      transformResponse: (response: GeneratedOntologiesResponse) => response.entries || [],
      providesTags: (result) => [
        { type: 'GeneratedOntologies' as const, id: 'LIST' },
        ...(result || []).map(({ id }) => ({ type: 'GeneratedOntology' as const, id: normalizeString(id) })),
      ],
    }),
    getGeneratedOntology: builder.query<GeneratedOntology, string>({
      query: (ontologyId) => ({
        url: `/api/v1/ontologies/${ontologyId}`,
        method: 'GET',
      }),
      providesTags: (result, error, ontologyId) => [
        { type: 'GeneratedOntology' as const, id: normalizeString(ontologyId) },
      ],
    }),
    queryGeneratedOntology: builder.mutation<OntologyQueryResponse, { ontologyId: string; question: string }>({
      query: ({ ontologyId, question }) => ({
        url: `/api/v1/ontologies/${ontologyId}/query`,
        method: 'POST',
        body: { question },
      }),
    }),
    getOntologyGenerationPlan: builder.query<
      OntologyGenerationPlan,
      { source_type: 'DataConnection' | 'Lakehouse'; source_id: string; force?: boolean; layer_id?: string }
    >({
      query: ({ source_type, source_id, force, layer_id }) => {
        const typeMap: Record<string, string> = {
          DataConnection: 'data-connection',
          Lakehouse: 'lakehouse',
        };
        const pathSegment = typeMap[source_type] || source_type.toLowerCase();
        const params: Record<string, string> = {};
        if (force) params.force = 'true';
        if (layer_id) params.layer_id = layer_id;
        return {
          url: `/api/v1/ontologies/generate/plan/${pathSegment}/${source_id}`,
          method: 'GET',
          params: Object.keys(params).length ? params : undefined,
        };
      },
      providesTags: (_result, _error, { source_id }) => [
        { type: 'GeneratedOntologies' as const, id: 'PLAN' },
        { type: 'GeneratedOntologies' as const, id: `PLAN-${normalizeString(source_id)}` },
      ],
      keepUnusedDataFor: 0,
    }),
    updateGeneratedOntology: builder.mutation<GeneratedOntology, { ontologyId: string; ontology: Ontology }>({
      query: ({ ontologyId, ontology }) => ({
        url: `/api/v1/ontologies/${ontologyId}`,
        method: 'PUT',
        body: {
          name: ontology.name,
          description: ontology.description,
          ontology_json: ontology,
        },
      }),
      invalidatesTags: (result, error, { ontologyId }) => [
        { type: 'GeneratedOntology' as const, id: normalizeString(ontologyId) },
        { type: 'GeneratedOntologies' as const, id: 'LIST' },
      ],
    }),
    generateOntology: builder.mutation<{ ontology: Ontology }, { description: string }>({
      query: (body) => ({
        url: '/api/generate-ontology',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'GeneratedOntologies' as const, id: 'LIST' },
        { type: 'GeneratedOntologies' as const, id: 'PLAN' },
      ],
    }),
    triggerOntologyGeneration: builder.mutation<
      { ontology_id?: string; status: string },
      { source_type: string; source_id: string; force?: boolean; layer_id?: string }
    >({
      query: ({ source_type, source_id, force, layer_id }) => {
        const typeMap: Record<string, string> = {
          DataConnection: 'data-connection',
          Lakehouse: 'lakehouse',
        };
        const pathSegment = typeMap[source_type] || source_type.toLowerCase();
        const params: Record<string, string> = {};
        if (force) params.force = 'true';
        if (layer_id) params.layer_id = layer_id;
        return {
          url: `/api/v1/ontologies/generate/${pathSegment}/${source_id}`,
          method: 'POST',
          params: Object.keys(params).length ? params : undefined,
        };
      },
      invalidatesTags: (_result, _error, { source_id }) => [
        { type: 'GeneratedOntologies' as const, id: 'LIST' },
        { type: 'GeneratedOntologies' as const, id: 'PLAN' },
        { type: 'GeneratedOntologies' as const, id: `PLAN-${normalizeString(source_id)}` },
      ],
    }),
  }),
});

export const {
  useGetGeneratedOntologiesQuery,
  useLazyGetGeneratedOntologyQuery,
  useQueryGeneratedOntologyMutation,
  useUpdateGeneratedOntologyMutation,
  useGenerateOntologyMutation,
  useGetOntologyGenerationPlanQuery,
  useTriggerOntologyGenerationMutation,
} = ontologyApiSlice;
